// src/controllers/useLeadsController.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  leadService,
  CRM_STATUS_PIPELINE,
  CRM_SOURCES,
  CRM_REQUIREMENTS,
  CRM_CITIES,
  CRM_CALL_OUTCOMES,
  getLeadStatusLabel
} from '../services/leadService';
import { useAuth } from './auth';

export const useLeadsController = () => {
  const { user, adminProfile, logAction } = useAuth();

  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [propertiesList, setPropertiesList] = useState([]);

  // Selected Lead & Detail Drawer
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadActivities, setLeadActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // New Note in Detail Drawer
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteType, setNewNoteType] = useState('call');
  const [savingNote, setSavingNote] = useState(false);

  // Call Outcome Logging
  const [callOutcome, setCallOutcome] = useState('Interested - Send Details');
  const [callOutcomeNotes, setCallOutcomeNotes] = useState('');
  const [callFollowupDate, setCallFollowupDate] = useState('');
  const [savingCallOutcome, setSavingCallOutcome] = useState(false);
  const [roundRobinLoading, setRoundRobinLoading] = useState(false);

  // Forward to Owner(s) Modal State (Rule 4)
  const [forwardModalOpen, setForwardModalOpen] = useState(false);
  const [matchingProperties, setMatchingProperties] = useState([]);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [forwardingLoading, setForwardingLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [requirementFilter, setRequirementFilter] = useState('all');
  const [assignedStaffFilter, setAssignedStaffFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Manual Add Lead Modal
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    name: '',
    mobile: '',
    email: '',
    source: 'manual',
    requirement: 'Buy a Property',
    city: 'Ghazipur',
    property_type: 'Residential',
    budget: '',
    property_id: '',
    assigned_to: '',
    priority: 'Warm',
    notes: ''
  });
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [addingLead, setAddingLead] = useState(false);
  const [formError, setFormError] = useState('');

  // Confirmation Modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmType, setConfirmType] = useState('warning');
  const [pendingAction, setPendingAction] = useState(null);

  // Fetch all initial data
  const fetchLeadsData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, staffRes, propsRes] = await Promise.all([
        leadService.getCentralLeads(),
        leadService.getAdmins(),
        leadService.getPropertiesForLead()
      ]);

      setLeads(leadsRes.data || []);
      setStaffList(staffRes.data || []);
      setPropertiesList(propsRes.data || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeadsData();
  }, [fetchLeadsData]);

  // Load activities when selected lead changes
  const handleSelectLead = async (lead) => {
    setSelectedLead(lead);
    setNewNoteText('');
    if (!lead) {
      setLeadActivities([]);
      return;
    }

    setActivitiesLoading(true);
    const { data } = await leadService.getLeadActivities(lead.id);
    setLeadActivities(data || []);
    setActivitiesLoading(false);
  };

  // Filter logic
  const filteredLeads = useMemo(() => {
    let result = [...leads];

    // Role-based scoping: Sales Executive sees only their assigned leads
    if (adminProfile?.role === 'Sales Executive') {
      result = result.filter(l => l.assigned_to === user?.id);
    }

    // Search query
    if (search.trim() !== '') {
      const q = search.toLowerCase();
      result = result.filter(l =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.mobile || '').toLowerCase().includes(q) ||
        (l.email || '').toLowerCase().includes(q) ||
        (l.requirement || '').toLowerCase().includes(q) ||
        (l.city || '').toLowerCase().includes(q) ||
        (l.property?.title || '').toLowerCase().includes(q) ||
        (l.budget || '').toLowerCase().includes(q) ||
        (l.notes || '').toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(l => (l.status || 'New').toLowerCase() === statusFilter.toLowerCase());
    }

    // City filter
    if (cityFilter !== 'all') {
      result = result.filter(l => (l.city || '').toLowerCase() === cityFilter.toLowerCase());
    }

    // Source filter
    if (sourceFilter !== 'all') {
      result = result.filter(l => (l.source || '').toLowerCase() === sourceFilter.toLowerCase());
    }

    // Requirement filter
    if (requirementFilter !== 'all') {
      result = result.filter(l => (l.requirement || '').toLowerCase() === requirementFilter.toLowerCase());
    }

    // Assigned Staff filter
    if (assignedStaffFilter === 'mine') {
      result = result.filter(l => l.assigned_to === user?.id);
    } else if (assignedStaffFilter === 'unassigned') {
      result = result.filter(l => !l.assigned_to);
    } else if (assignedStaffFilter !== 'all') {
      result = result.filter(l => l.assigned_to === assignedStaffFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let cutoff = new Date();
      if (dateFilter === 'today') cutoff.setHours(0, 0, 0, 0);
      else if (dateFilter === 'week') cutoff.setDate(now.getDate() - 7);
      else if (dateFilter === 'month') cutoff.setMonth(now.getMonth() - 1);
      result = result.filter(l => new Date(l.created_at) >= cutoff);
    }

    return result;
  }, [leads, search, statusFilter, cityFilter, sourceFilter, requirementFilter, assignedStaffFilter, dateFilter, user, adminProfile]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, cityFilter, sourceFilter, requirementFilter, assignedStaffFilter, dateFilter]);

  // Pagination calculation
  const totalLeads = filteredLeads.length;
  const totalPages = Math.ceil(totalLeads / pageSize) || 1;
  const paginatedLeads = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredLeads.slice(startIndex, startIndex + pageSize);
  }, [filteredLeads, currentPage, pageSize]);

  // Handle Form Change with Debounced Duplicate Mobile Check
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setNewLeadForm(prev => ({ ...prev, [name]: value }));

    if (name === 'mobile') {
      const clean = value.replace(/\D/g, '').slice(-10);
      if (clean.length === 10) {
        setCheckingDuplicate(true);
        leadService.checkDuplicateMobile(clean).then(res => {
          setCheckingDuplicate(false);
          if (res.isDuplicate) {
            setDuplicateWarning(res.existingLead);
          } else {
            setDuplicateWarning(null);
          }
        });
      } else {
        setDuplicateWarning(null);
      }
    }
  };

  // Submit Manual Lead
  const handleCreateLead = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!newLeadForm.name || newLeadForm.name.trim().length < 2) {
      setFormError('Customer full name is required');
      return;
    }

    const cleanMobile = newLeadForm.mobile.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanMobile)) {
      setFormError('Please enter a valid 10-digit Indian mobile number (e.g. 9876543210)');
      return;
    }

    setAddingLead(true);
    try {
      const payload = {
        ...newLeadForm,
        mobile: cleanMobile,
        property_id: newLeadForm.property_id || null,
        assigned_to: newLeadForm.assigned_to || null
      };

      const result = await leadService.createManualLead(payload);
      if (result.error) {
        setFormError(result.error.message || 'Failed to create lead');
        return;
      }

      setShowAddLeadModal(false);
      setNewLeadForm({
        name: '',
        mobile: '',
        email: '',
        source: 'manual',
        requirement: 'Buy a Property',
        city: 'Ghazipur',
        property_type: 'Residential',
        budget: '',
        property_id: '',
        assigned_to: '',
        priority: 'Warm',
        notes: ''
      });
      setDuplicateWarning(null);
      await fetchLeadsData();
      await logAction(user?.id, adminProfile?.full_name || 'Staff', 'Create Lead', result.data?.lead_id, `Manually created lead for ${newLeadForm.name}`);
    } catch (err) {
      setFormError(err.message || 'Failed to create lead');
    } finally {
      setAddingLead(false);
    }
  };

  // Update Status with Activity Tracking
  const handleUpdateLeadStatus = (leadId, nextStatus) => {
    setConfirmTitle('Update Lead Status');
    setConfirmMessage(`Are you sure you want to move this lead to stage "${nextStatus}"?`);
    setConfirmType('warning');
    setPendingAction(() => async () => {
      const { error } = await leadService.updateCrmLead(leadId, { status: nextStatus });
      if (error) throw error;

      setLeads(prev => prev.map(l => (l.id === leadId ? { ...l, status: nextStatus } : l)));
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: nextStatus }));
        const { data } = await leadService.getLeadActivities(leadId);
        setLeadActivities(data || []);
      }

      await logAction(user?.id, adminProfile?.full_name || 'Staff', 'Update Lead Status', leadId, `Stage changed to "${nextStatus}"`);
    });
    setConfirmOpen(true);
  };

  // Update Priority
  const handleUpdatePriority = async (leadId, nextPriority) => {
    const { error } = await leadService.updateCrmLead(leadId, { priority: nextPriority });
    if (error) {
      alert(error.message || 'Failed to update priority');
      return;
    }

    setLeads(prev => prev.map(l => (l.id === leadId ? { ...l, priority: nextPriority } : l)));
    if (selectedLead && selectedLead.id === leadId) {
      setSelectedLead(prev => ({ ...prev, priority: nextPriority }));
    }
  };

  // Assign Staff
  const handleAssignLead = async (leadId, staffId) => {
    const staff = staffList.find(s => s.id === staffId);
    const staffName = staff ? staff.full_name : null;

    const { error } = await leadService.updateCrmLead(leadId, {
      assigned_to: staffId || null,
      note: staffId ? `Assigned lead to ${staffName}` : 'Unassigned lead',
      activity_type: 'assignment'
    });

    if (error) {
      alert(error.message || 'Failed to assign lead');
      return;
    }

    setLeads(prev => prev.map(l => (l.id === leadId ? {
      ...l,
      assigned_to: staffId || null,
      assigned_admin: staff ? { id: staff.id, full_name: staff.full_name, role: staff.role } : null
    } : l)));

    if (selectedLead && selectedLead.id === leadId) {
      setSelectedLead(prev => ({
        ...prev,
        assigned_to: staffId || null,
        assigned_admin: staff ? { id: staff.id, full_name: staff.full_name, role: staff.role } : null
      }));
      const { data } = await leadService.getLeadActivities(leadId);
      setLeadActivities(data || []);
    }

    await logAction(user?.id, adminProfile?.full_name || 'Staff', 'Assign Lead', leadId, staffId ? `Assigned to ${staffName}` : 'Unassigned');
  };

  // Add Activity Note
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNoteText.trim() || !selectedLead) return;

    setSavingNote(true);
    const { error } = await leadService.addLeadActivityNote(
      selectedLead.id,
      newNoteText.trim(),
      newNoteType
    );
    setSavingNote(false);

    if (error) {
      alert(error.message || 'Failed to save activity note');
      return;
    }

    setNewNoteText('');
    const { data } = await leadService.getLeadActivities(selectedLead.id);
    setLeadActivities(data || []);
  };

  // Log Call Outcome with Automatic Status Transition and Next Follow-up
  const handleLogCallOutcome = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedLead) return;

    setSavingCallOutcome(true);
    try {
      const matched = CRM_CALL_OUTCOMES.find(o => o.label === callOutcome);
      const nextStatus = matched ? matched.nextStatus : null;

      const { data, error } = await leadService.logCallOutcome(selectedLead.id, {
        outcome: callOutcome,
        notes: callOutcomeNotes,
        next_status: nextStatus,
        follow_up_date: callFollowupDate ? new Date(callFollowupDate).toISOString() : null
      });

      if (error) throw error;

      // Update in memory
      setLeads(prev => prev.map(l => (l.id === selectedLead.id ? {
        ...l,
        status: nextStatus || l.status,
        follow_up_date: callFollowupDate ? new Date(callFollowupDate).toISOString() : l.follow_up_date
      } : l)));

      setSelectedLead(prev => ({
        ...prev,
        status: nextStatus || prev.status,
        follow_up_date: callFollowupDate ? new Date(callFollowupDate).toISOString() : prev.follow_up_date
      }));

      setCallOutcomeNotes('');
      setCallFollowupDate('');

      const activitiesRes = await leadService.getLeadActivities(selectedLead.id);
      setLeadActivities(activitiesRes.data || []);

      await logAction(
        user?.id,
        adminProfile?.full_name || 'Staff',
        'Log Call Outcome',
        selectedLead.id,
        `Logged outcome "${callOutcome}"${nextStatus ? ` -> status "${nextStatus}"` : ''}`
      );
    } catch (err) {
      alert(err.message || 'Failed to log call outcome');
    } finally {
      setSavingCallOutcome(false);
    }
  };

  // Auto-Assign Round-Robin across active Sales Staff
  const handleAutoAssignRoundRobin = async () => {
    setRoundRobinLoading(true);
    try {
      const { data, error } = await leadService.assignRoundRobin(null);
      if (error) throw error;

      alert(data?.message || 'Round-robin assignment completed successfully!');
      await fetchLeadsData();
      await logAction(
        user?.id,
        adminProfile?.full_name || 'Super Admin',
        'Auto Assign Leads',
        'all',
        'Triggered round-robin lead distribution across sales staff'
      );
    } catch (err) {
      alert(err.message || 'Failed to run round-robin assignment');
    } finally {
      setRoundRobinLoading(false);
    }
  };

  // Open Forward to Owner(s) Modal (Rule 4)
  const handleOpenForwardModal = async () => {
    if (!selectedLead) return;
    setForwardModalOpen(true);
    setMatchingLoading(true);
    setSelectedPropertyIds([]);
    try {
      const { data, error } = await leadService.getMatchingPropertiesForLead(
        selectedLead.city,
        selectedLead.property_type
      );
      if (error) throw error;
      setMatchingProperties(data || []);
    } catch (err) {
      console.error(err);
      alert('Failed to load matching properties');
    } finally {
      setMatchingLoading(false);
    }
  };

  // Toggle Property Selection
  const handleTogglePropertySelect = (propertyId) => {
    setSelectedPropertyIds(prev => 
      prev.includes(propertyId) ? prev.filter(id => id !== propertyId) : [...prev, propertyId]
    );
  };

  // Confirm Forward to Selected Owners
  const handleConfirmForward = async () => {
    if (!selectedLead || selectedPropertyIds.length === 0) {
      alert('Please select at least one property owner to forward this lead.');
      return;
    }

    setForwardingLoading(true);
    try {
      const result = await leadService.forwardLeadToOwners(
        selectedLead.id,
        selectedPropertyIds,
        user?.id
      );

      if (result.success) {
        alert(result.message || `Lead forwarded successfully to ${result.count || selectedPropertyIds.length} property owner(s)!`);
        setForwardModalOpen(false);
        setSelectedPropertyIds([]);
        // Refresh activities for this lead
        const acts = await leadService.getLeadActivities(selectedLead.id);
        setLeadActivities(acts);
        await logAction(
          user?.id,
          adminProfile?.full_name || 'Super Admin',
          'Forward Lead',
          selectedLead.id,
          `Forwarded lead to ${selectedPropertyIds.length} property owners`
        );
      } else {
        alert(result.error || 'Failed to forward lead.');
      }
    } catch (err) {
      alert(err.message || 'Error forwarding lead.');
    } finally {
      setForwardingLoading(false);
    }
  };

  // Confirm Modal Executer
  const executeConfirmAction = async () => {
    if (!pendingAction) return;
    try {
      await pendingAction();
      setConfirmOpen(false);
      setPendingAction(null);
    } catch (err) {
      alert(err.message || 'Action failed');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (filteredLeads.length === 0) {
      alert('No leads to export');
      return;
    }

    const headers = [
      'Lead ID', 'Name', 'Mobile', 'Email', 'Source', 'Requirement',
      'City', 'Property Type', 'Budget', 'Property Title', 'Assigned To',
      'Status / Stage', 'Priority', 'Created Date'
    ];

    const rows = filteredLeads.map(l => [
      l.id,
      `"${(l.name || '').replace(/"/g, '""')}"`,
      l.mobile || '',
      `"${(l.email || '').replace(/"/g, '""')}"`,
      `"${(l.source || 'manual').replace(/"/g, '""')}"`,
      `"${(l.requirement || '').replace(/"/g, '""')}"`,
      l.city || '',
      l.property_type || '',
      `"${(l.budget || '').replace(/"/g, '""')}"`,
      `"${(l.property?.title || '').replace(/"/g, '""')}"`,
      `"${(l.assigned_admin?.full_name || 'Unassigned').replace(/"/g, '""')}"`,
      l.status || 'New',
      l.priority || 'Warm',
      new Date(l.created_at).toLocaleString()
    ]);

    const csvContent = "data:text/csv;charset=utf-8,"
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `central_leads_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    loading,
    leads,
    filteredLeads,
    paginatedLeads,
    totalLeads,
    totalPages,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    staffList,
    propertiesList,
    selectedLead,
    handleSelectLead,
    leadActivities,
    activitiesLoading,
    newNoteText,
    setNewNoteText,
    newNoteType,
    setNewNoteType,
    savingNote,
    handleAddNote,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    cityFilter,
    setCityFilter,
    sourceFilter,
    setSourceFilter,
    requirementFilter,
    setRequirementFilter,
    assignedStaffFilter,
    setAssignedStaffFilter,
    dateFilter,
    setDateFilter,
    showAddLeadModal,
    setShowAddLeadModal,
    newLeadForm,
    handleFormChange,
    duplicateWarning,
    checkingDuplicate,
    addingLead,
    formError,
    handleCreateLead,
    confirmOpen,
    setConfirmOpen,
    confirmTitle,
    confirmMessage,
    confirmType,
    executeConfirmAction,
    handleUpdateLeadStatus,
    handleUpdatePriority,
    handleAssignLead,
    handleExportCSV,
    fetchLeadsData,
    currentUser: user,
    currentAdminProfile: adminProfile,
    // CRM Phase 2: Call Outcomes & Round Robin
    callOutcome,
    setCallOutcome,
    callOutcomeNotes,
    setCallOutcomeNotes,
    callFollowupDate,
    setCallFollowupDate,
    savingCallOutcome,
    handleLogCallOutcome,
    roundRobinLoading,
    handleAutoAssignRoundRobin,
    callOutcomesList: CRM_CALL_OUTCOMES,
    // Forward to Owner(s)
    forwardModalOpen,
    setForwardModalOpen,
    matchingProperties,
    matchingLoading,
    selectedPropertyIds,
    forwardingLoading,
    handleOpenForwardModal,
    handleTogglePropertySelect,
    handleConfirmForward
  };
};
